// INVDESIGN1-S2 (docs/build-briefs/INVDESIGN1-S2-home-setup-review-order.md; owner-approved mockups invdesign1-1, -2 and -6).
// Pure, no DOM: what the Inventory home says. Every figure here is a join of reads that already exist (the home-areas read, the
// stock-value report, the cart proposals, the purchase orders); nothing is recomputed about stock. The screen is
// ui/src/inventoryHome.ts, and test/invdesign1-s2-home-model.test.ts proves each rule without a browser.

import type {
  HomeAreaDto,
  InventoryHomeAreasDto,
  PurchaseOrderSummaryDto,
  StockValueReportDto,
  WhatToOrderDto,
} from "./api.ts";
import { formatCartMoney, formatCountQuantity, purchaseOrderIsReceivable } from "./purchasingDisplay.ts";

/** Where "Build <vendor> order" and "Edit amounts" go: the ordering screens (INVDESIGN1-ORDER). Build saves that vendor's suggested
 * lines as a draft and opens it; Edit opens What to order on that vendor's card. */
export type OrderTarget =
  | { readonly kind: "build-order"; readonly vendorId: string }
  | { readonly kind: "edit-amounts"; readonly vendorId: string };

export type ChipTone = "ok" | "low" | "act" | "neutral" | "info";
export interface Chip {
  readonly text: string;
  readonly tone: ChipTone;
}

export type AreaMark = "done" | "now" | "todo";

export interface AreaRowView {
  /** The count section id; null is the Unassigned group. */
  readonly areaId: string | null;
  readonly name: string;
  readonly position: number;
  readonly mark: AreaMark;
  readonly itemsText: string;
  readonly detail: string;
  readonly chips: readonly Chip[];
  /** Setup only: counted of total for the meter; absent on the running home. */
  readonly progress?: { readonly done: number; readonly total: number };
  readonly actionLabel: string;
  /** True for the one area the big action points at. */
  readonly primary: boolean;
}

// ---------------------------------------------------------------------
// Words
// ---------------------------------------------------------------------

function plural(count: number, one: string, many: string): string {
  return `${count} ${count === 1 ? one : many}`;
}

/** "2:51 PM" for a time on the same day as `nowMs`, "Oct 26" for any other. The viewer's own clock, like the rest of the app. */
export function whenText(iso: string, nowMs: number): string {
  const at = new Date(iso);
  const now = new Date(nowMs);
  const sameDay =
    at.getFullYear() === now.getFullYear() &&
    at.getMonth() === now.getMonth() &&
    at.getDate() === now.getDate();
  return sameDay
    ? new Intl.DateTimeFormat("en-US", { hour: "numeric", minute: "2-digit" }).format(at)
    : new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric" }).format(at);
}

function byWho(name: string | null): string {
  return name === null ? "" : ` by ${name}`;
}

/** "the back shelf" for "Back shelf"; the unassigned group reads as what it is. */
function areaPhrase(area: Pick<HomeAreaDto, "id" | "name">): string {
  return area.id === null ? "items with no area" : area.name.toLowerCase();
}

// ---------------------------------------------------------------------
// Mode: setup or home. The rule itself is the server's (startingStockPending); the screen only reads it.
// ---------------------------------------------------------------------

export type HomeMode = "setup" | "home";
export function homeMode(areas: Pick<InventoryHomeAreasDto, "startingStockPending">): HomeMode {
  return areas.startingStockPending ? "setup" : "home";
}

// ---------------------------------------------------------------------
// Low: below par, the stock-value report's own flag.
// ---------------------------------------------------------------------

/** An item is low when the report says it is below par and it is not an item nobody has given starting stock yet. An item at zero
 * that has a par is low (it is the most below par there is); the report's own "at par" case is not. */
export function isBelowPar(item: StockValueReportDto["items"][number]): boolean {
  if (!item.belowPar || item.noStartingStock || item.belowZero) return false;
  if (item.onHand === null || item.par === null) return true;
  const comparable = item.parUnit === null || item.parUnit === item.onHandUnit;
  return !(comparable && Math.abs(Number(item.onHand) - Number(item.par)) < 1e-9);
}

export function lowIds(report: StockValueReportDto | undefined): ReadonlySet<string> | undefined {
  if (report === undefined) return undefined;
  return new Set(report.items.filter(isBelowPar).map((item) => item.liquorProductId));
}

// ---------------------------------------------------------------------
// The next area
// ---------------------------------------------------------------------

/** What the open count has not yet reached in this area. */
function itemsLeft(area: HomeAreaDto): number {
  return Math.max(0, area.itemIds.length - area.countedInOpenCount);
}

/** The first area, in walking order, with something left to count in the open count (or, with none open, the first area). */
export function nextArea(areas: readonly HomeAreaDto[], hasOpenCount: boolean): HomeAreaDto | undefined {
  if (!hasOpenCount) return areas[0];
  return areas.find((area) => itemsLeft(area) > 0);
}

// ---------------------------------------------------------------------
// The running home (screen 1)
// ---------------------------------------------------------------------

export interface HeroView {
  readonly eyebrow: string;
  readonly title: string;
  readonly body: string;
  readonly progress?: { readonly done: number; readonly total: number; readonly text: string };
  readonly actionLabel: string;
  /** Which area the action opens (undefined: the first with something left). */
  readonly areaId?: string | null;
  readonly kind: "start" | "resume";
}

export function heroView(areas: InventoryHomeAreasDto, nowMs: number): HeroView {
  const total = areas.itemCount;
  const last = areas.lastClosedCount;
  const lastText =
    last === null ? "" : ` Last closed count: ${whenText(last.closedAt, nowMs)}${byWho(last.closedByName)}.`;
  const open = areas.openCount;
  if (open === null) {
    return {
      eyebrow: "This week's count",
      title: "Start this week's count",
      body: `Walk the shelves area by area, then order what is low.${lastText}`,
      actionLabel: "Start count",
      kind: "start",
    };
  }
  const next = nextArea(areas.areas, true);
  const progress = {
    done: open.countedItems,
    total,
    text: `${open.countedItems} of ${total} items counted`,
  };
  if (next === undefined) {
    return {
      eyebrow: "This week's count",
      title: "Everything is counted",
      body: `Review the count, check what is off, then close it.${lastText}`,
      progress,
      actionLabel: "Resume count",
      kind: "resume",
    };
  }
  const after = areas.areas.slice(areas.areas.indexOf(next) + 1).filter((area) => itemsLeft(area) > 0);
  const rest = after.length === 0 ? "" : `, then ${joinWords(after.map((a) => (a.id === null ? areaPhrase(a) : `the ${areaPhrase(a)}`)))}`;
  return {
    eyebrow: "This week's count",
    title: `Count the ${areaPhrase(next)}`,
    body: `${plural(itemsLeft(next), "item", "items")} left on the ${areaPhrase(next)}${rest}.${lastText}`,
    progress,
    actionLabel: "Resume count",
    areaId: next.id,
    kind: "resume",
  };
}

function joinWords(words: readonly string[]): string {
  if (words.length <= 1) return words.join("");
  return `${words.slice(0, -1).join(", ")} and ${words[words.length - 1] ?? ""}`;
}

export function areaRowsForHome(
  areas: InventoryHomeAreasDto,
  low: ReadonlySet<string> | undefined,
  nowMs: number,
): AreaRowView[] {
  const next = nextArea(areas.areas, areas.openCount !== null);
  return areas.areas.map((area, index) => {
    const total = area.itemIds.length;
    const lowCount = low === undefined ? undefined : area.itemIds.filter((id) => low.has(id)).length;
    const chips: Chip[] = [];
    let detail: string;
    let mark: AreaMark;
    let actionLabel: string;
    if (areas.openCount !== null) {
      const counted = area.countedInOpenCount;
      const finished = counted >= total;
      const left = area.closedCountLast;
      if (finished) {
        mark = "done";
        chips.push({ text: "Counted", tone: "ok" });
        const stamp = area.openCountLast;
        detail =
          stamp === null
            ? plural(total, "item", "items")
            : `${plural(total, "item", "items")} · Counted ${whenText(stamp.at, nowMs)}${byWho(stamp.by)}`;
        actionLabel = "Recount";
      } else if (counted > 0) {
        mark = "now";
        chips.push({ text: "Counting", tone: "act" });
        detail = `${plural(total, "item", "items")} · In progress, ${counted} of ${total}`;
        actionLabel = area === next ? `Count the ${areaPhrase(area)}` : "Count";
      } else {
        mark = "todo";
        chips.push({ text: "Not counted yet", tone: "neutral" });
        detail =
          left === null
            ? plural(total, "item", "items")
            : `${plural(total, "item", "items")} · Last ${whenText(left.at, nowMs)}${left.by === null ? "" : `, ${left.by}`}`;
        actionLabel = area === next ? `Count the ${areaPhrase(area)}` : "Count";
      }
    } else {
      mark = "todo";
      const left = area.closedCountLast;
      if (left === null) {
        chips.push({ text: "Not counted yet", tone: "neutral" });
        detail = plural(total, "item", "items");
      } else {
        chips.push({ text: `Counted ${whenText(left.at, nowMs)}`, tone: "neutral" });
        detail = `${plural(total, "item", "items")} · Last count${byWho(left.by)}`;
      }
      actionLabel = "Count";
    }
    if (lowCount !== undefined) {
      chips.push(
        lowCount > 0
          ? { text: mark === "now" ? `${lowCount} low so far` : `${lowCount} low`, tone: "low" }
          : { text: "No low items", tone: "neutral" },
      );
    }
    return {
      areaId: area.id,
      name: area.name,
      position: index + 1,
      mark,
      itemsText: plural(total, "item", "items"),
      detail,
      chips,
      actionLabel,
      primary: area === next,
    };
  });
}

// ---------------------------------------------------------------------
// The setup home (screen 2)
// ---------------------------------------------------------------------

/** Items of the area that already have a starting figure: stock history, or a line in the open count. */
export function setItems(area: HomeAreaDto): number {
  return Math.min(area.itemIds.length, area.itemsWithStartingStock + area.countedInOpenCount);
}

export interface SetupView {
  readonly areasDone: number;
  readonly areasTotal: number;
  readonly steps: ReadonlyArray<{ readonly label: string; readonly done: boolean }>;
  readonly actionLabel: string;
  readonly actionAreaId?: string | null;
  readonly rows: readonly AreaRowView[];
}

export function setupView(areas: InventoryHomeAreasDto): SetupView {
  const rows: AreaRowView[] = [];
  let firstOpen: HomeAreaDto | undefined;
  for (const area of areas.areas) {
    if (firstOpen === undefined && setItems(area) < area.itemIds.length) firstOpen = area;
  }
  let done = 0;
  areas.areas.forEach((area, index) => {
    const total = area.itemIds.length;
    const have = setItems(area);
    const complete = have >= total;
    if (complete) done += 1;
    const chips: Chip[] = [
      complete
        ? { text: "Starting stock set", tone: "ok" }
        : have > 0
          ? { text: "Setting now", tone: "act" }
          : { text: "Not counted yet", tone: "neutral" },
    ];
    rows.push({
      areaId: area.id,
      name: area.name,
      position: index + 1,
      mark: complete ? "done" : area === firstOpen ? "now" : "todo",
      itemsText: plural(total, "item", "items"),
      detail: plural(total, "item", "items"),
      chips,
      progress: { done: have, total },
      actionLabel: complete ? "Edit" : have > 0 ? "Continue" : "Start",
      primary: area === firstOpen,
    });
  });
  const actionLabel =
    firstOpen === undefined
      ? "Resume count"
      : `Count the ${areaPhrase(firstOpen)}`;
  return {
    areasDone: done,
    areasTotal: areas.areas.length,
    steps: [
      { label: "Name your areas", done: areas.namedAreaCount > 0 },
      { label: "Add items", done: areas.itemCount > 0 },
      { label: "Count each area", done: false },
    ],
    actionLabel,
    ...(firstOpen === undefined ? {} : { actionAreaId: firstOpen.id }),
    rows,
  };
}

// ---------------------------------------------------------------------
// To order, by vendor (screens 1 and 6)
// ---------------------------------------------------------------------

export interface OrderLineView {
  readonly name: string;
  readonly hasText: string;
  readonly orderText: string;
  readonly note: string | null;
  readonly cents: number | null;
}

export interface VendorOrderView {
  readonly vendorId: string;
  readonly vendorName: string;
  readonly lines: readonly OrderLineView[];
  /** The What to order read's own vendor total in whole cents, so this screen and that one always show the same figure. */
  readonly totalCents: number;
  /** Lines with no cost on the offer; the read leaves them out of the total, and the card says so. */
  readonly unpriced: number;
}

function trimNumber(raw: string | null | undefined): string {
  if (raw === null || raw === undefined) return "0";
  const value = Number(raw);
  if (!Number.isFinite(value)) return raw;
  return String(Math.round(value * 100) / 100);
}

/** What is low, by vendor, straight from the What to order read (ordering screens): its lines, its order units and its totals. */
export function ordersByVendor(data: WhatToOrderDto | undefined): {
  vendors: VendorOrderView[];
  needsSetup: number;
} {
  if (data === undefined) return { vendors: [], needsSetup: 0 };
  const vendors = data.vendors.map((vendor) => ({
    vendorId: vendor.vendorId,
    vendorName: vendor.vendorName,
    totalCents: vendor.totalCents,
    unpriced: vendor.lines.filter((line) => line.lineEstimateCents === null).length,
    lines: vendor.lines.map(
      (line): OrderLineView => ({
        name: line.productName,
        hasText: `has ${trimNumber(line.onHand)}`,
        orderText: formatCountQuantity(Number(line.quantity), line.orderUnit.toLowerCase()),
        note: null,
        cents: line.lineEstimateCents,
      }),
    ),
  }));
  return { vendors, needsSetup: data.needsSetup.length };
}

export function vendorTotalText(vendor: VendorOrderView): string {
  return vendor.unpriced === vendor.lines.length ? "No cost set" : formatCartMoney(vendor.totalCents);
}

export function buildButtonLabel(vendorName: string): string {
  return `Build ${vendorName} order`;
}

// ---------------------------------------------------------------------
// On the way
// ---------------------------------------------------------------------

export function onTheWay(orders: readonly PurchaseOrderSummaryDto[] | undefined): PurchaseOrderSummaryDto[] {
  if (orders === undefined) return [];
  return orders.filter((order) => purchaseOrderIsReceivable(order.status));
}
